// biome-ignore lint/style/noRestrictedImports: imperative call from Zustand action, batched seen-flush not eligible for a React Query hook.
import { type MarkSeenData, markSeen } from 'sdk';
import { appConfig, isSeenTracked, type ProductEntityType, type SeenTrackedProductType } from 'shared';
import { isRecord } from 'shared/utils/as-record';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { isDebugMode } from '~/env';
import { reportCriticalError } from '~/lib/tracing';
import { applyUnseenDelta } from '~/modules/seen/unseen-delta';
import { idbKvStorage } from '~/query/idb-kv-storage';

export type SeenMeta = { tenantId: string; organizationId: string; channelId: string; productType: ProductEntityType; productId: string };

interface SeenBatch {
  tenantId: string;
  organizationId: string;
  productType: SeenTrackedProductType;
  productIds: Set<string>;
}

const flushIntervalMs = appConfig.mode === 'development' ? 10 * 1000 : 60 * 1000;

/** Cap on persisted flushed IDs, evicting oldest first (Sets iterate in insertion order). Evicted IDs may be re-sent; markSeen skips duplicates. */
const flushedIdsMax = 10_000;

const batchKey = (organizationId: string, productType: ProductEntityType) => `${organizationId}:${productType}`;

const pending = new Map<string, SeenBatch>();
/** Ids of a flush whose request has not answered: in neither `pending` nor `flushedIds`, yet seen here. */
const inFlight = new Set<string>();
let flushIntervalId: ReturnType<typeof setInterval> | null = null;

interface SeenStoreState {
  flushedIds: Set<string>;

  markProductSeen: (meta: SeenMeta) => void;
  startFlushInterval: () => void;
  stopFlushInterval: () => void;
  flush: () => Promise<void>;
  /** Reset in-memory state to initial (call on sign-out; persisted data lives in localUserDb). */
  reset: () => void;
}

/** Seen entities queued from IntersectionObserver, batch-flushed to POST /:tenantId/:organizationId/seen; pending IDs stay in memory, confirmed IDs persist. */
export const seenStore = createStore<SeenStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        flushedIds: new Set(),

        markProductSeen: ({ tenantId, organizationId, channelId, productType, productId }) => {
          if (!isSeenTracked(productType)) return;
          if (get().flushedIds.has(productId) || inFlight.has(productId)) return;

          const key = batchKey(organizationId, productType);
          const batch = pending.get(key) ?? { tenantId, organizationId, productType, productIds: new Set() };
          if (batch.productIds.has(productId)) return;
          batch.productIds.add(productId);
          pending.set(key, batch);

          applyUnseenDelta(channelId, productType, -1);
          if (isDebugMode) console.debug('[SeenStore] queued:', productType, productId.slice(0, 8));
        },

        startFlushInterval: () => {
          if (flushIntervalId) return;
          flushIntervalId = setInterval(() => get().flush(), flushIntervalMs);
        },

        stopFlushInterval: () => {
          if (flushIntervalId) clearInterval(flushIntervalId);
          flushIntervalId = null;
        },

        flush: async () => {
          if (pending.size === 0) return;
          // Snapshot and clear optimistically; failed batches merge back for the next attempt.
          const batches = [...pending.values()];
          pending.clear();
          for (const batch of batches) for (const id of batch.productIds) inFlight.add(id);
          if (isDebugMode)
            console.debug(
              '[SeenStore] flushing',
              batches.map((batch) => `${batch.productType}(${batch.productIds.size})`),
            );

          for (const batch of batches) {
            const { tenantId, organizationId, productType } = batch;
            try {
              const productIds = [...batch.productIds];
              await markSeen({ path: { tenantId, organizationId }, body: { entityIds: productIds, entityType: productType } });

              const flushedIds = new Set(get().flushedIds);
              for (const id of productIds) flushedIds.add(id);
              for (const id of flushedIds) {
                if (flushedIds.size <= flushedIdsMax) break;
                flushedIds.delete(id);
              }
              set({ flushedIds });
            } catch (error) {
              reportCriticalError('seen.flush_failed', error, { product_type: productType });
              const requeued = pending.get(batchKey(organizationId, productType));
              if (requeued) for (const id of batch.productIds) requeued.productIds.add(id);
              else pending.set(batchKey(organizationId, productType), batch);
            } finally {
              for (const id of batch.productIds) inFlight.delete(id);
            }
          }
          // No refetch needed: markProductSeen already patched unseen counts, and SSE covers external changes.
        },

        reset: () => {
          pending.clear();
          inFlight.clear();
          get().stopFlushInterval();
          set({ flushedIds: new Set() });
        },
      }),
      {
        version: 1,
        name: 'seen',
        skipHydration: true,
        storage: createJSONStorage(() => idbKvStorage('seen')),
        // Sets serialize as arrays; insertion order (oldest first) survives the round-trip.
        partialize: (state) => ({ flushedIds: [...state.flushedIds] }),
        merge: (persisted, current) => ({
          ...current,
          flushedIds: new Set(
            isRecord(persisted) && Array.isArray(persisted.flushedIds)
              ? persisted.flushedIds.filter((id): id is string => typeof id === 'string')
              : [],
          ),
        }),
      },
    ),
    { enabled: isDebugMode, name: 'seen store' },
  ),
);

/** True when this client saw the entity (flushed, in flight or still queued); store internals stay private behind this accessor. */
export function isSeenLocally(entityId: string): boolean {
  if (seenStore.getState().flushedIds.has(entityId) || inFlight.has(entityId)) return true;
  for (const batch of pending.values()) if (batch.productIds.has(entityId)) return true;
  return false;
}

/** Flush pending seen data via sendBeacon on page unload. Call once at app init. */
export function setupSeenBeaconFlush() {
  const handler = () => {
    for (const batch of pending.values()) {
      const body = JSON.stringify({ entityIds: [...batch.productIds], entityType: batch.productType } satisfies MarkSeenData['body']);
      navigator.sendBeacon(`/api/${batch.tenantId}/${batch.organizationId}/seen`, new Blob([body], { type: 'application/json' }));
    }
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}
